import type {
  PurchaseListQuery,
  PurchaseOrderRow,
  PurchaseOrderItemRow,
  PurchaseOrderWithItems,
  PurchaseReceptionRow,
  PurchaseReceptionItemRow,
  PurchaseReceptionWithItems,
  PurchaseReturnRow,
  PurchaseReturnItemRow,
  PurchasesSummary,
  PurchaseOrderStatus,
} from '../domain/purchaseTypes';
import { PURCHASES_LIST_PAGE_SIZE } from '../domain/purchaseTypes';
import { matchesSearchAny } from '../../shared/format/searchText';

function mapPurchaseOrderRow(r: Record<string, unknown>): PurchaseOrderRow {
  return {
    id: String(r.id ?? ''),
    branchId: String(r.branch_id ?? ''),
    supplierId: r.supplier_id != null ? String(r.supplier_id) : null,
    supplierName: r.supplier_name != null ? String(r.supplier_name) : null,
    status: (r.status ?? 'draft') as PurchaseOrderStatus,
    origin: r.origin === 'direct' ? 'direct' : 'order',
    orderNumber: r.order_number != null ? String(r.order_number) : null,
    total: Number(r.total ?? 0),
    notes: r.notes != null ? String(r.notes) : null,
    expectedDelivery: r.expected_delivery != null ? String(r.expected_delivery) : null,
    supplierInvoiceRef: r.supplier_invoice_ref != null ? String(r.supplier_invoice_ref) : null,
    createdAt: String(r.created_at ?? ''),
    createdBy: r.created_by != null ? String(r.created_by) : null,
    updatedAt: String(r.updated_at ?? ''),
    updatedBy: r.updated_by != null ? String(r.updated_by) : null,
    receivedAt: r.received_at != null ? String(r.received_at) : null,
    receivedBy: r.received_by != null ? String(r.received_by) : null,
    voidedAt: r.voided_at != null ? String(r.voided_at) : null,
    voidedBy: r.voided_by != null ? String(r.voided_by) : null,
    voidReason: r.void_reason != null ? String(r.void_reason) : null,
    closedAt: r.closed_at != null ? String(r.closed_at) : null,
    closedBy: r.closed_by != null ? String(r.closed_by) : null,
    closeReason: r.close_reason != null ? String(r.close_reason) : null,
  };
}

function mapPurchaseOrderItemRow(r: Record<string, unknown>): PurchaseOrderItemRow {
  return {
    id: String(r.id ?? ''),
    purchaseOrderId: String(r.purchase_order_id ?? ''),
    productId: r.product_id != null ? String(r.product_id) : null,
    productName: r.product_name != null ? String(r.product_name) : null,
    productSku: r.product_sku != null ? String(r.product_sku) : null,
    quantity: Number(r.quantity ?? 0),
    unitCost: r.unit_cost != null ? Number(r.unit_cost) : null,
    unitOfMeasure: r.unit_of_measure != null ? String(r.unit_of_measure) : null,
    receivedQuantity: Number(r.received_quantity ?? 0),
    targetBranchId: r.target_branch_id != null ? String(r.target_branch_id) : null,
    // #694: variante de destino del ítem (null = parent)
    variantId: r.variant_id != null ? String(r.variant_id) : null,
  };
}

function mapReceptionRow(r: Record<string, unknown>): PurchaseReceptionRow {
  return {
    id: String(r.id ?? ''),
    branchId: String(r.branch_id ?? ''),
    purchaseOrderId: r.purchase_order_id != null ? String(r.purchase_order_id) : null,
    supplierId: r.supplier_id != null ? String(r.supplier_id) : null,
    supplierName: r.supplier_name != null ? String(r.supplier_name) : null,
    status: (r.status ?? 'active') as PurchaseReceptionRow['status'],
    receivedAt: String(r.received_at ?? ''),
    receivedBy: r.received_by != null ? String(r.received_by) : null,
    notes: r.notes != null ? String(r.notes) : null,
    supplierInvoiceRef: r.supplier_invoice_ref != null ? String(r.supplier_invoice_ref) : null,
    voidedAt: r.voided_at != null ? String(r.voided_at) : null,
    voidedBy: r.voided_by != null ? String(r.voided_by) : null,
    voidReason: r.void_reason != null ? String(r.void_reason) : null,
    createdAt: String(r.created_at ?? ''),
  };
}

function mapReceptionItemRow(r: Record<string, unknown>): PurchaseReceptionItemRow {
  return {
    id: String(r.id ?? ''),
    receptionId: String(r.reception_id ?? ''),
    purchaseOrderItemId: r.purchase_order_item_id != null ? String(r.purchase_order_item_id) : null,
    productId: r.product_id != null ? String(r.product_id) : null,
    productName: r.product_name != null ? String(r.product_name) : null,
    productSku: r.product_sku != null ? String(r.product_sku) : null,
    quantity: Number(r.quantity ?? 0),
    unitCost: r.unit_cost != null ? Number(r.unit_cost) : null,
    unitOfMeasure: r.unit_of_measure != null ? String(r.unit_of_measure) : null,
    stockLotId: r.stock_lot_id != null ? String(r.stock_lot_id) : null,
    stockMovementId: r.stock_movement_id != null ? String(r.stock_movement_id) : null,
    targetBranchId: r.target_branch_id != null ? String(r.target_branch_id) : null,
    returnedQuantity: Number(r.returned_quantity ?? 0),
  };
}

function mapReturnRow(r: Record<string, unknown>): PurchaseReturnRow {
  return {
    id: String(r.id ?? ''),
    branchId: String(r.branch_id ?? ''),
    receptionId: r.reception_id != null ? String(r.reception_id) : null,
    supplierId: r.supplier_id != null ? String(r.supplier_id) : null,
    supplierName: r.supplier_name != null ? String(r.supplier_name) : null,
    status: (r.status ?? 'active') as PurchaseReturnRow['status'],
    returnedAt: String(r.returned_at ?? ''),
    returnedBy: r.returned_by != null ? String(r.returned_by) : null,
    reason: r.reason != null ? String(r.reason) : null,
    notes: r.notes != null ? String(r.notes) : null,
    voidedAt: r.voided_at != null ? String(r.voided_at) : null,
    voidedBy: r.voided_by != null ? String(r.voided_by) : null,
    voidReason: r.void_reason != null ? String(r.void_reason) : null,
    createdAt: String(r.created_at ?? ''),
  };
}

function mapReturnItemRow(r: Record<string, unknown>): PurchaseReturnItemRow {
  return {
    id: String(r.id ?? ''),
    returnId: String(r.return_id ?? ''),
    receptionItemId: r.reception_item_id != null ? String(r.reception_item_id) : null,
    productId: r.product_id != null ? String(r.product_id) : null,
    productName: r.product_name != null ? String(r.product_name) : null,
    productSku: r.product_sku != null ? String(r.product_sku) : null,
    quantity: Number(r.quantity ?? 0),
    unitCost: r.unit_cost != null ? Number(r.unit_cost) : null,
    unitOfMeasure: r.unit_of_measure != null ? String(r.unit_of_measure) : null,
    stockMovementId: r.stock_movement_id != null ? String(r.stock_movement_id) : null,
  };
}

function buildWhereClause(query: PurchaseListQuery): { where: string; params: unknown[] } {
  const conditions: string[] = ['branch_id = ?'];
  const params: unknown[] = [query.branchId];

  if (query.queue === 'to_receive') {
    conditions.push("status IN ('sent', 'partial')");
  } else if (query.status && query.status !== 'all') {
    conditions.push('status = ?');
    params.push(query.status);
  }
  if (query.queue === 'to_pay' || query.queue === 'overdue') {
    const ids = query.queueOrderIds ?? [];
    if (ids.length === 0) {
      conditions.push('1 = 0');
    } else {
      conditions.push(`id IN (${ids.map(() => '?').join(',')})`);
      params.push(...ids);
    }
  }
  if (query.supplierId && query.supplierId !== 'all') {
    conditions.push('supplier_id = ?');
    params.push(query.supplierId);
  }
  if (query.origin && query.origin !== 'all') {
    conditions.push('origin = ?');
    params.push(query.origin);
  }
  if (query.dateFrom) {
    conditions.push('created_at >= ?');
    params.push(query.dateFrom);
  }
  if (query.dateTo) {
    conditions.push('created_at <= ?');
    params.push(query.dateTo);
  }
  if (query.search) {
    conditions.push('(supplier_name LIKE ? OR notes LIKE ? OR supplier_invoice_ref LIKE ? OR order_number LIKE ?)');
    const like = `%${query.search}%`;
    params.push(like, like, like, like);
  }

  return { where: conditions.join(' AND '), params };
}

export async function listPurchaseOrdersForBranch(
  query: PurchaseListQuery,
): Promise<PurchaseOrderRow[]> {
    const rows = await filterPurchaseOrdersFromCache(query);
        const sortBy = query.sortBy ?? 'date';
        const sortDir = query.sortDir === 'asc' ? 1 : -1;
        rows.sort((a, b) => {
          const av =
            sortBy === 'total' ? a.total : sortBy === 'supplier' ? a.supplierName ?? '' : a.createdAt;
          const bv =
            sortBy === 'total' ? b.total : sortBy === 'supplier' ? b.supplierName ?? '' : b.createdAt;
          if (av < bv) return -1 * sortDir;
          if (av > bv) return 1 * sortDir;
          return 0;
        });
        const limit = query.limit ?? PURCHASES_LIST_PAGE_SIZE;
        const offset = query.offset ?? 0;
        return rows.slice(offset, offset + limit);
}

async function filterPurchaseOrdersFromCache(query: PurchaseListQuery): Promise<PurchaseOrderRow[]> {
  const { getWebPurchasesSnapshotCache } = await import('./webPurchasesSnapshotCache');
  const cached = getWebPurchasesSnapshotCache();
  if (!cached) return [];
  // Agregados de ítems por OC (una pasada) para alimentar la fila de lista.
  const itemsByOrder = new Map<string, { count: number; ordered: number; received: number }>();
  for (const raw of cached.purchaseOrderItems ?? []) {
    const orderId = String(raw.purchase_order_id ?? '');
    if (!orderId) continue;
    const agg = itemsByOrder.get(orderId) ?? { count: 0, ordered: 0, received: 0 };
    agg.count += 1;
    agg.ordered += Number(raw.quantity ?? 0);
    agg.received += Number(raw.received_quantity ?? 0);
    itemsByOrder.set(orderId, agg);
  }
  let rows = cached.purchaseOrders
    .filter((raw) => String(raw.branch_id ?? '') === query.branchId)
    .map((raw) => {
      const row = mapPurchaseOrderRow(raw);
      const agg = itemsByOrder.get(row.id);
      if (agg) {
        row.itemsCount = agg.count;
        row.orderedUnits = agg.ordered;
        row.receivedUnits = agg.received;
      }
      return row;
    });
  if (query.queue === 'to_receive') {
    rows = rows.filter((r) => r.status === 'sent' || r.status === 'partial');
  } else if (query.status && query.status !== 'all') {
    rows = rows.filter((r) => r.status === query.status);
  }
  if (query.queue === 'to_pay' || query.queue === 'overdue') {
    const ids = new Set(query.queueOrderIds ?? []);
    rows = ids.size === 0 ? [] : rows.filter((r) => ids.has(r.id));
  }
  if (query.supplierId && query.supplierId !== 'all') {
    rows = rows.filter((r) => r.supplierId === query.supplierId);
  }
  if (query.origin && query.origin !== 'all') {
    rows = rows.filter((r) => r.origin === query.origin);
  }
  if (query.dateFrom) rows = rows.filter((r) => r.createdAt >= query.dateFrom!);
  if (query.dateTo) rows = rows.filter((r) => r.createdAt <= query.dateTo!);
  if (query.search?.trim()) {
    rows = rows.filter((r) =>
      matchesSearchAny([r.supplierName, r.orderNumber, r.notes, r.supplierInvoiceRef], query.search ?? ''),
    );
  }
  return rows;
}

export async function countPurchaseOrdersForBranch(query: PurchaseListQuery): Promise<number> {
    const rows = await filterPurchaseOrdersFromCache(query);
        return rows.length;
}

export async function getPurchaseOrderById(id: string): Promise<PurchaseOrderWithItems | null> {
    const { getWebPurchasesSnapshotCache } = await import('./webPurchasesSnapshotCache');
        const cached = getWebPurchasesSnapshotCache();
        if (!cached) return null;
        const poRaw = cached.purchaseOrders.find((r) => String(r.id) === id);
        if (!poRaw) return null;
        const order = mapPurchaseOrderRow(poRaw);
        const items = cached.purchaseOrderItems
          .filter((r) => String(r.purchase_order_id) === id)
          .map(mapPurchaseOrderItemRow)
          .sort((a, b) => a.id.localeCompare(b.id));
        return { ...order, items };
}

export async function listPurchaseOrderItems(purchaseOrderId: string): Promise<PurchaseOrderItemRow[]> {
    const detail = await getPurchaseOrderById(purchaseOrderId);
        return detail?.items ?? [];
}

export async function listReceptionsForBranch(
  branchId: string,
  limit = 50,
  offset = 0,
): Promise<PurchaseReceptionRow[]> {
    const { getWebPurchasesSnapshotCache } = await import('./webPurchasesSnapshotCache');
        const cached = getWebPurchasesSnapshotCache();
        if (!cached) return [];
        return cached.receptions
          .filter((r) => String(r.branch_id ?? '') === branchId)
          .map(mapReceptionRow)
          .sort((a, b) => (a.receivedAt < b.receivedAt ? 1 : -1))
          .slice(offset, offset + limit);
}

export async function getReceptionById(id: string): Promise<PurchaseReceptionWithItems | null> {
    const { getWebPurchasesSnapshotCache } = await import('./webPurchasesSnapshotCache');
        const cached = getWebPurchasesSnapshotCache();
        if (!cached) return null;
        const raw = cached.receptions.find((r) => String(r.id) === id);
        if (!raw) return null;
        const reception = mapReceptionRow(raw);
        const items = cached.receptionItems
          .filter((r) => String(r.reception_id) === id)
          .map(mapReceptionItemRow)
          .sort((a, b) => a.id.localeCompare(b.id));
        return { ...reception, items };
}

export async function listReceptionsForOrder(
  purchaseOrderId: string,
): Promise<PurchaseReceptionWithItems[]> {
    const { getWebPurchasesSnapshotCache } = await import('./webPurchasesSnapshotCache');
        const cached = getWebPurchasesSnapshotCache();
        if (!cached) return [];
        const receptions = cached.receptions
          .filter(
            (r) =>
              String(r.purchase_order_id ?? '') === purchaseOrderId && String(r.status ?? '') === 'active'
          )
          .map(mapReceptionRow)
          .sort((a, b) => (a.receivedAt < b.receivedAt ? 1 : -1));
        return receptions.map((reception) => ({
          ...reception,
          items: cached.receptionItems
            .filter((r) => String(r.reception_id) === reception.id)
            .map(mapReceptionItemRow)
            .sort((a, b) => a.id.localeCompare(b.id)),
        }));
}

export async function listReturnsForBranch(
  branchId: string,
  limit = 50,
  offset = 0,
): Promise<PurchaseReturnRow[]> {
    const { getWebPurchasesSnapshotCache } = await import('./webPurchasesSnapshotCache');
        const cached = getWebPurchasesSnapshotCache();
        if (!cached) return [];
        return cached.returns
          .filter((r) => String(r.branch_id ?? '') === branchId)
          .map(mapReturnRow)
          .sort((a, b) => (a.returnedAt < b.returnedAt ? 1 : -1))
          .slice(offset, offset + limit);
}

// BUG-45: Listar devoluciones ligadas a una recepción (para anular desde el detail)
export async function listReturnsForReception(
  receptionId: string,
): Promise<PurchaseReturnRow[]> {
    const { getWebPurchasesSnapshotCache } = await import('./webPurchasesSnapshotCache');
        const cached = getWebPurchasesSnapshotCache();
        if (!cached) return [];
        return cached.returns
          .filter(
            (r) => String(r.reception_id ?? '') === receptionId && String(r.status ?? '') === 'active'
          )
          .map(mapReturnRow)
          .sort((a, b) => (a.returnedAt < b.returnedAt ? 1 : -1));
}

export async function listReturnItemsForReturn(returnId: string): Promise<PurchaseReturnItemRow[]> {
  const { getWebPurchasesSnapshotCache } = await import('./webPurchasesSnapshotCache');
  const cached = getWebPurchasesSnapshotCache();
  if (!cached) return [];
  return cached.returnItems
    .filter((r) => String(r.return_id ?? '') === returnId)
    .map(mapReturnItemRow);
}

/** Same supplier, same invoice text (trim, case-insensitive). Caller shows a warning, does not block. */
export async function findDuplicateSupplierInvoiceRef(params: {
  supplierId: string;
  invoiceRef: string;
  excludeOrderId?: string | null;
}): Promise<{ id: string; orderNumber: string | null } | null> {
  const normalized = params.invoiceRef.trim().toLowerCase();
  if (!normalized || !params.supplierId) return null;
    const { getWebPurchasesSnapshotCache } = await import('./webPurchasesSnapshotCache');
        const cached = getWebPurchasesSnapshotCache();
        if (!cached) return null;
        const hit = cached.purchaseOrders
          .map(mapPurchaseOrderRow)
          .find(
            (o) =>
              o.supplierId === params.supplierId &&
              (o.supplierInvoiceRef ?? '').trim().toLowerCase() === normalized &&
              (!params.excludeOrderId || o.id !== params.excludeOrderId)
          );
        return hit ? { id: hit.id, orderNumber: hit.orderNumber } : null;
}

export async function hasActiveReturnForOrder(purchaseOrderId: string): Promise<boolean> {
    const { getWebPurchasesSnapshotCache } = await import('./webPurchasesSnapshotCache');
        const cached = getWebPurchasesSnapshotCache();
        if (!cached) return false;
        const receptionIds = new Set(
          cached.receptions
            .filter((r) => String(r.purchase_order_id ?? '') === purchaseOrderId)
            .map((r) => String(r.id))
        );
        return cached.returns.some(
          (r) => receptionIds.has(String(r.reception_id ?? '')) && String(r.status ?? '') === 'active'
        );
}

export async function summarizePurchasesForBranch(
  branchId: string,
  dateFrom?: string,
  dateTo?: string,
): Promise<PurchasesSummary> {
    const { getWebPurchasesSnapshotCache } = await import('./webPurchasesSnapshotCache');
        const cached = getWebPurchasesSnapshotCache();
        const orders = (cached?.purchaseOrders ?? [])
          .map(mapPurchaseOrderRow)
          .filter((o) => {
            if (o.branchId !== branchId) return false;
            if (dateFrom && o.createdAt < dateFrom) return false;
            if (dateTo && o.createdAt > dateTo) return false;
            return true;
          });
        return {
          totalOrders: orders.length,
          // Monto comprometido: excluye borradores (todavía no son compra) y anuladas.
          totalAmount: orders.reduce(
            (sum, o) => sum + (o.status !== 'cancelled' && o.status !== 'draft' ? o.total : 0),
            0,
          ),
          pendingReception: orders.filter((o) => o.status === 'sent' || o.status === 'partial').length,
          partialReception: orders.filter((o) => o.status === 'partial').length,
          received: orders.filter((o) => o.status === 'received').length,
          cancelled: orders.filter((o) => o.status === 'cancelled').length,
          // Las colas de pago se calculan en el hook a partir de los gastos ligados.
          toPay: 0,
          toPayAmount: 0,
          overdue: 0,
        };
}
